import React from 'react';
import styled from '@emotion/styled';
import { Col, Divider, Row, Space } from 'antd';
import { Title } from 'app/components/Typography/Title';
import { InfoLine, Status } from '../Elements';
import ReferenceMedia from '../ReferenceMedia';
import { get, isEmpty } from 'lodash';
import { IShippingInformation } from 'app/types/interfaces/Product';
import { urlify } from 'app/utils';
import { Paragraph } from 'app/components/Typography/Paragraph';

const Container = styled.div`
  margin-top: 20px;
  max-width: 60%;
  padding: 12px;
  border: 1px solid #c4c8d0;
  border-radius: 4px;
  margin-bottom: 14px;
  .ant-divider-horizontal {
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;
const Content = styled.div`
  margin: 12px;
  background: #fafafa;
  padding: 8px;
`;
const Info = styled.div``;
const InfoItem = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'haveDifferent'
})<{
  haveDifferent?: boolean;
}>`
  font-size: 12px;
  font-weight: 400;
  text-align: left;
  color: #8991a0;
  > div.ant-col:last-of-type {
    text-align: right;
  }
  &.actual__product__info {
    > div.ant-col:last-of-type {
      label {
        color: #232b3a;
      }
    }
  }
  &.barcode {
    > div.ant-col:last-of-type {
      .ant-space-item:first-of-type {
        label {
          color: ${(props) => (props.haveDifferent ? '#ff961e' : '#232b3a')};
        }
      }
    }
  }
`;
const InfoLabel = styled(Col)``;
type Props = {
  title: string;
  extra?: React.ReactNode;
  value: any;
  shippingInformation: IShippingInformation;
};

const PackingMarkingItemInfo = (props: {
  shippingInformation: IShippingInformation;
  value: any;
}) => {
  const { shippingInformation, value } = props;
  // product master length 10cm,
  // actual 11cm
  // (Actual - PM)/PM x 100%
  // example: (11-10)/10 x 100% = +10%
  // if +10% is within tolerance , mark in green.
  // if +10% is exceed tolerance, mark in red

  const renderStatus = (
    data: number,
    productData: number,
    ranges: number[]
  ) => {
    const computedProgress = ((data - productData) / productData) * 100;
    const progress =
      computedProgress === Infinity || isNaN(computedProgress)
        ? 0.0
        : computedProgress;

    const upper = ranges[0] || 0;
    const lower = ranges[1] || 0;
    if (progress >= lower && progress <= upper) {
      return (
        <Status
          status="passed"
          renderLabel={() => {
            return `${parseFloat(`${progress}`).toFixed(2)}%`;
          }}
        />
      );
    }

    if (progress < lower || progress > upper) {
      return (
        <Status
          status="failed"
          renderLabel={() => {
            return `${parseFloat(`${progress}`).toFixed(2)}%`;
          }}
        />
      );
    }
    return (
      <Status
        status={'pending'}
        renderLabel={() => {
          return `${
            isNaN(progress) ? '-' : parseFloat(`${progress}`).toFixed(2)
          }%`;
        }}
      ></Status>
    );
  };
  return (
    <Info>
      <Title level={5}>Product Info Reference</Title>
      <InfoItem>
        <InfoLabel span={12}>Dim. Tolerance (Length)</InfoLabel>
        <InfoLabel span={12}>
          {`+${shippingInformation?.lengthUpperTolerance ?? ''}% ; -${
            shippingInformation?.lengthLowerTolerance ?? ''
          }% / ${shippingInformation?.length ?? ''}${
            shippingInformation.lengthUnit ?? ''
          }`}
        </InfoLabel>
      </InfoItem>
      <InfoItem>
        <InfoLabel span={12}>Dim. Tolerance (Width)</InfoLabel>
        <InfoLabel span={12}>{`+${
          shippingInformation?.widthUpperTolerance ?? ''
        }% ; -${shippingInformation?.widthLowerTolerance ?? ''}% / ${
          shippingInformation?.width ?? ''
        }${shippingInformation.widthUnit ?? ''}`}</InfoLabel>
      </InfoItem>
      <InfoItem>
        <InfoLabel span={12}>Dim. Tolerance (Height)</InfoLabel>
        <InfoLabel span={12}>{`+${
          shippingInformation?.heightUpperTolerance ?? ''
        }% ; -${shippingInformation?.heightLowerTolerance ?? ''}% / ${
          shippingInformation?.height ?? ''
        }${shippingInformation.heightUnit ?? ''}`}</InfoLabel>
      </InfoItem>
      <InfoItem>
        <InfoLabel span={12}>Net Weight Tolerance</InfoLabel>
        <InfoLabel span={12}>{`+${
          shippingInformation?.netWeightUpperTolerance ?? ''
        }% ; -${shippingInformation?.netWeightLowerTolerance ?? ''}% / ${
          shippingInformation?.netWeight ?? ''
        }${shippingInformation.netWeightUnit ?? ''}`}</InfoLabel>
      </InfoItem>
      <InfoItem>
        <InfoLabel span={12}>Gross Weight Tolerance</InfoLabel>
        <InfoLabel span={12}>
          {`+${shippingInformation?.grossWeightUpperTolerance ?? ''}% ; -${
            shippingInformation?.grossWeightLowerTolerance ?? ''
          }% / ${shippingInformation?.grossWeight ?? ''}${
            shippingInformation.grossWeightUnit ?? ''
          }`}
        </InfoLabel>
      </InfoItem>
      <Divider></Divider>
      <Title level={5}>Actual Product Info</Title>
      <InfoItem className="actual__product__info">
        <InfoLabel span={12}>Length</InfoLabel>
        <InfoLabel span={12}>
          <Space size={2}>
            <>
              {renderStatus(
                value.length || 0,
                parseFloat(shippingInformation.length || '0'),
                [
                  parseFloat(shippingInformation.lengthUpperTolerance || '0'),
                  parseFloat(shippingInformation.lengthLowerTolerance || '0')
                ]
              )}
            </>
            <label>
              /{` ${value.length || 0} ${shippingInformation.lengthUnit ?? ''}`}
            </label>
          </Space>
        </InfoLabel>
      </InfoItem>
      <InfoItem className="actual__product__info">
        <InfoLabel span={12}>Width</InfoLabel>
        <InfoLabel span={12}>
          <Space size={2}>
            <>
              {renderStatus(
                value.width || 0,
                parseFloat(shippingInformation.width || '0'),
                [
                  parseFloat(shippingInformation.widthUpperTolerance || '0'),
                  parseFloat(shippingInformation.widthLowerTolerance || '0')
                ]
              )}
            </>
            <label>
              /{` ${value.width || 0} ${shippingInformation.widthUnit || ''}`}
            </label>
          </Space>
        </InfoLabel>
      </InfoItem>
      <InfoItem className="actual__product__info">
        <InfoLabel span={12}>Height</InfoLabel>
        <InfoLabel span={12}>
          <Space size={2}>
            <>
              {renderStatus(
                value.height || 0,
                parseFloat(shippingInformation.height || '0'),
                [
                  parseFloat(shippingInformation.heightUpperTolerance || '0'),
                  parseFloat(shippingInformation.heightLowerTolerance || '0')
                ]
              )}
            </>
            <label>
              /{` ${value.height || 0} ${shippingInformation.heightUnit || ''}`}
            </label>
          </Space>
        </InfoLabel>
      </InfoItem>
      <InfoItem className="actual__product__info">
        <InfoLabel span={12}>Net Weight</InfoLabel>
        <InfoLabel span={12}>
          <Space size={2}>
            <>
              {renderStatus(
                value.netWeight || 0,
                parseFloat(shippingInformation.netWeight || '0'),
                [
                  parseFloat(
                    shippingInformation.netWeightUpperTolerance || '0'
                  ),
                  parseFloat(shippingInformation.netWeightLowerTolerance || '0')
                ]
              )}
            </>
            <label>
              /
              {` ${value.netWeight || 0} ${
                shippingInformation.netWeightUnit || ''
              }`}
            </label>
          </Space>
        </InfoLabel>
      </InfoItem>
      <InfoItem className="actual__product__info">
        <InfoLabel span={12}>Gross Weight</InfoLabel>
        <InfoLabel span={12}>
          <Space size={2}>
            <>
              {renderStatus(
                value.grossWeight || 0,
                parseFloat(shippingInformation.grossWeight || '0'),
                [
                  parseFloat(
                    shippingInformation.grossWeightUpperTolerance || '0'
                  ),
                  parseFloat(
                    shippingInformation.grossWeightLowerTolerance || '0'
                  )
                ]
              )}
            </>
            <label>
              /
              {` ${value.grossWeight || 0} ${
                shippingInformation.grossWeightUnit || ''
              }`}
            </label>
          </Space>
        </InfoLabel>
      </InfoItem>
      <Divider></Divider>
      <InfoItem
        className="barcode"
        haveDifferent={value.actualBarcode !== shippingInformation.barcode}
      >
        <InfoLabel span={10}>Barcode</InfoLabel>
        <InfoLabel span={14}>
          <Space size={2}>
            <label>{value.actualBarcode}</label>
            <label>/ {shippingInformation.barcode}</label>
          </Space>
        </InfoLabel>
      </InfoItem>
    </Info>
  );
};
const PackingMarkingItem: React.FC<Props> = ({
  title,
  extra,
  value,
  shippingInformation
}) => {
  const { length, width, height } = value;

  return (
    <Container>
      <Space className="header" direction="vertical" size={16}>
        <Title level={5}>{title}</Title>
        <InfoLine
          label="Result: "
          value={<Status status={value?.result || 'pending'}></Status>}
        ></InfoLine>
        <InfoLine
          label="Actual Dimensions "
          value={`${length || 0}x${width || 0}x${height || 0}${
            shippingInformation.lengthUnit || ''
          }`}
        ></InfoLine>
        {extra}
      </Space>
      <Content>
        <PackingMarkingItemInfo
          value={value}
          shippingInformation={shippingInformation}
        ></PackingMarkingItemInfo>
      </Content>
      <div>
        <InfoLine
          label="Remark"
          direction="vertical"
          align="start"
          style={{ marginBottom: 16 }}
          size={4}
          value={
            !isEmpty(value?.remark) ? (
              <Paragraph style={{ marginBottom: 0 }}>
                <p
                  style={{ marginBottom: 0 }}
                  dangerouslySetInnerHTML={{
                    __html: `<div>${urlify(value?.remark || 'N/A')}</div>`
                  }}
                ></p>
              </Paragraph>
            ) : (
              'N/A'
            )
          }
        ></InfoLine>
      </div>
      <ReferenceMedia media={get(value, 'referenceMedia', [])}></ReferenceMedia>
    </Container>
  );
};

export default PackingMarkingItem;
